import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  MutationType,
  SurveyCountResponseType,
  SurveysResponseType,
  SurveyType,
} from 'shared/types';
import qs from 'query-string';
import request from 'utils/request';
import { get, isNil } from 'lodash';
import { SURVEYS_PER_PAGE } from './constant';

export const useGetAllSurveys = ({
  filters = {},
  callback,
  pageToken,
  pageIndex,
  isNewFilter,
}: MutationType & {
  filters: any;
  pageToken: string;
  pageIndex?: number;
  isNewFilter: boolean;
}) =>
  useQuery<SurveysResponseType>(
    'surveyList',
    async () => {
      const newParams = filters.term
        ? {
            ...(filters || {}),
            page_size: SURVEYS_PER_PAGE,
            page: (isNewFilter ? 0 : pageIndex || 0) + 1,
          }
        : {
            ...(filters || {}),
            page_size: SURVEYS_PER_PAGE,
            is_template: false,
            ...(pageToken ? { next_token: pageToken } : {}),
          };
      const newURL = qs.stringifyUrl({
        url: newParams.term ? 'surveys/search' : 'surveys',
        query: newParams,
      });
      const data: SurveysResponseType = await request(newURL);
      const countURL = get(data, ['_links', 'count', 'href'], '');
      const countReqURL = countURL
        ? countURL.split('api/v1/surveyserv/')?.[1]
        : '';
      let countResponse: any;
      if (countReqURL && !filters?.term) {
        countResponse = await request(countReqURL);
      } else if (filters.term || isNewFilter) {
        countResponse = {
          count: data.surveys?.length || data.metadata?.count || 0,
        };
      }
      return {
        ...(data || {}),
        _links: { ...(data._links || {}), count: countResponse.count || 0 },
      };
    },
    {
      onSuccess: (response) => {
        if (callback) callback(true, response);
      },
      onError: (error) => {
        if (callback) callback(false, error);
      },
    },
  );

export const useGetAllActiveSurveysCount = ({ callback }: MutationType) => {
  return useQuery<number>(
    ['active-surveys-count'],
    async () => {
      const data: SurveyCountResponseType = await request(
        `surveys/count?is_template=false&is_active=true&state=ACTIVE`,
      );
      return isNil(data.count) ? NaN : Number(data.count);
    },
    {
      onSuccess: (response: number) => {
        if (callback) callback(true, response);
      },
      onError: (err) => {
        if (callback) callback(false, err);
      },
    },
  );
};

export const useGetAllSurveysCount = ({ callback }: MutationType) => {
  return useQuery<number>(
    ['surveys-count'],
    async () => {
      const data: SurveyCountResponseType = await request(
        `surveys/count?is_template=false`,
      );
      return isNil(data.count) ? NaN : Number(data.count);
    },
    {
      onSuccess: (response: number) => {
        if (callback) callback(true, response);
      },
      onError: (err) => {
        if (callback) callback(false, err);
      },
    },
  );
};

export const useDeleteSurvey = ({ setToast, callback }: MutationType) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (survey: SurveyType) => {
      if (!survey.id) return;
      await request(`surveys/${survey.id}`, {}, 'DELETE');
    },
    {
      onSuccess: (response: any, variables: SurveyType) => {
        queryClient.invalidateQueries('surveyCount');
        queryClient.invalidateQueries('surveyList');
        queryClient.invalidateQueries('surveys-count');
        queryClient.invalidateQueries('active-surveys-count');
        if (setToast)
          setToast({
            delay: 3000,
            type: 'success',
            text: `Survey deleted successfully`,
          });
        if (callback) callback(true, response, variables);
      },
      onError: (error: any, variables: SurveyType) => {
        if (setToast)
          setToast({
            delay: 3000,
            type: 'error',
            text: `Survey deletion failed`,
          });
        if (callback) callback(false, error, variables);
      },
    },
  );
};

export const useGetSurveyResponseCount = (surveyId: string) => {
  return useQuery(
    [surveyId, 'response-count'],
    async () => {
      const data = await request(`surveys/${surveyId}/responses/count`);
      return data;
    },
    {
      refetchOnWindowFocus: true,
      staleTime: 10000,
      enabled: Boolean(surveyId),
    },
  );
};

export const getCollectorsInfo = async (id: string) => {
  const data = await request(`surveys/${id}/collectors`);
  return await data;
};
